import React from "react";
import { Link } from "react-router-dom";
import { COLORS, TYPOGRAPHY, SPACING, RADIUS, TRANSITIONS } from "../constants/designTokens";
import { SITE, CATEGORIES } from "../constants/siteConfig";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    services: CATEGORIES.map((cat) => ({ label: cat.title, href: cat.path })),
    company: [
      { label: "About Us", href: "/#about" },
      { label: "Our Process", href: "/#our-process" },
      { label: "Contact", href: "/#contact" },
    ],
    legal: [
      { label: "Privacy Policy", href: "/privacy" },
      { label: "Terms of Service", href: "/terms" },
    ],
  };

  const socialLinks = [
    {
      label: "LinkedIn",
      href: "https://linkedin.com/company/marine-sealink",
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
        </svg>
      ),
    },
    {
      label: "WhatsApp",
      href: SITE.whatsapp,
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
          <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0012.04 2z" />
        </svg>
      ),
    },
    {
      label: "Email",
      href: `mailto:${SITE.email}`,
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
          <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
        </svg>
      ),
    },
  ];

  const renderLink = (link) => {
    const isExternal = link.href.startsWith("http") || link.href.startsWith("mailto");
    if (isExternal) {
      return (
        <a
          key={link.label}
          href={link.href}
          className="footer-link"
          target={link.href.startsWith("http") ? "_blank" : undefined}
          rel={link.href.startsWith("http") ? "noopener noreferrer" : undefined}
        >
          {link.label}
        </a>
      );
    }
    return (
      <Link key={link.label} to={link.href} className="footer-link">
        {link.label}
      </Link>
    );
  };

  return (
    <footer
      className="footer-section"
      style={{
        background: `linear-gradient(135deg, ${COLORS.primary.dark} 0%, #0a1828 100%)`,
        color: COLORS.text.white,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <style>{`
        .footer-section {
          padding: 40px 0 18px 0;
        }
        .footer-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent 0%, rgba(64, 168, 196, 0.5) 50%, transparent 100%);
        }
        .footer-logo {
          max-width: 180px;
          height: auto;
          margin-bottom: 16px;
        }
        .footer-tagline {
          color: rgba(255, 255, 255, 0.7);
          font-size: 14px;
          line-height: 1.75;
          margin-bottom: 16px;
        }
        .footer-heading {
          color: ${COLORS.primary.teal};
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 16px;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        .footer-link {
          display: block;
          color: rgba(255, 255, 255, 0.8);
          font-size: 16px;
          text-decoration: none;
          padding: 4px 0;
          transition: ${TRANSITIONS.all};
          position: relative;
        }
        .footer-link::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 0;
          height: 1px;
          background: ${COLORS.primary.teal};
          transition: width 0.3s ease;
        }
        .footer-link:hover, .footer-link:focus {
          color: ${COLORS.primary.teal};
          text-decoration: none;
        }
        .footer-link:focus {
          outline: 2px solid ${COLORS.primary.teal};
          outline-offset: 2px;
          border-radius: 4px;
        }
        .footer-link:hover::after, .footer-link:focus::after {
          width: 100%;
        }
        .social-links {
          display: flex;
          gap: 12px;
          margin-top: 16px;
        }
        .social-link {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 44px;
          height: 44px;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(64, 168, 196, 0.3);
          border-radius: 50%;
          color: white;
          transition: ${TRANSITIONS.all};
          text-decoration: none;
        }
        .social-link:hover, .social-link:focus {
          background: ${COLORS.primary.teal};
          border-color: ${COLORS.primary.teal};
          color: white;
          transform: translateY(-3px);
          box-shadow: 0 4px 12px rgba(64, 168, 196, 0.4);
          text-decoration: none;
          outline: none;
        }
        .social-link:focus {
          outline: 2px solid ${COLORS.primary.teal};
          outline-offset: 2px;
        }
        .footer-contact {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(64, 168, 196, 0.2);
          border-radius: ${RADIUS.xl};
          padding: ${SPACING.lg} ${SPACING.xl};
        }
        .footer-contact p {
          margin-bottom: ${SPACING.xs};
        }
        .footer-contact h4 {
          color: ${COLORS.primary.teal};
          font-weight: 600;
          font-size: ${TYPOGRAPHY.fontSize.xl};
          margin-bottom: ${SPACING.md};
        }
        .footer-contact p {
          color: rgba(255, 255, 255, 0.8);
          margin-bottom: ${SPACING.sm};
          line-height: 1.6;
        }
        .footer-contact a {
          color: ${COLORS.primary.teal};
          text-decoration: none;
          transition: ${TRANSITIONS.colors};
        }
        .footer-contact a:hover {
          color: ${COLORS.primary.lightTeal};
          text-decoration: underline;
        }
        .footer-contact a:focus {
          outline: 2px solid ${COLORS.primary.teal};
          outline-offset: 2px;
          border-radius: 4px;
        }
        .footer-bottom {
          margin-top: ${SPACING.lg};
          padding-top: ${SPACING.md};
          border-top: 1px solid rgba(64, 168, 196, 0.2);
          text-align: center;
          color: rgba(255, 255, 255, 0.6);
          font-size: ${TYPOGRAPHY.fontSize.sm};
        }
        @media (max-width: 991.98px) {
          .footer-contact {
            margin-top: ${SPACING.xl};
          }
        }
      `}</style>

      <div className="container">
        <div className="row">
          <div className="col-lg-4 mb-5 mb-lg-0">
            <img src="/img/Logo-Full_White.png" alt="Marine Sealink B.V." className="footer-logo" />
            <p className="footer-tagline">
              {SITE.tagline}. Your trusted partner for sourcing and supplying quality marine spare
              parts: electrical, instrumentation &amp; control, OWS &amp; ODME, and fire &amp; smoke
              detection, delivered to vessels worldwide.
            </p>
            <div className="social-links">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  className="social-link"
                  target={social.href.startsWith("http") ? "_blank" : undefined}
                  rel={social.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  aria-label={social.label}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          <div className="col-lg-2 col-md-6 mb-4 mb-md-0">
            <h5 className="footer-heading">Spare Parts</h5>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {footerLinks.services.map(renderLink)}
            </ul>
          </div>

          <div className="col-lg-2 col-md-6 mb-4 mb-md-0">
            <h5 className="footer-heading">Company</h5>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {footerLinks.company.map(renderLink)}
            </ul>
          </div>

          <div className="col-lg-4">
            <h5 className="footer-heading">Contact Us</h5>
            <div className="footer-contact">
              <h4>Need a Quote?</h4>
              <p>Send us your parts list or equipment details and our team will source the right certified spares for your vessel.</p>
              <p>
                <strong>Email:</strong> <a href={`mailto:${SITE.email}`}>{SITE.email}</a>
              </p>
              <p>
                <strong>Mobile:</strong> <a href={SITE.phoneHref}>{SITE.phoneDisplay}</a>
              </p>
              <p>
                <strong>Office:</strong> {SITE.location}
              </p>
              <p style={{ marginTop: SPACING.md, fontSize: TYPOGRAPHY.fontSize.sm, opacity: 0.85 }}>
                <strong>KVK:</strong> {SITE.kvk} &nbsp;|&nbsp; <strong>VAT:</strong> {SITE.vat}
              </p>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p>© {currentYear} {SITE.name}. All rights reserved. &nbsp;|&nbsp; KVK {SITE.kvk} &nbsp;|&nbsp; VAT {SITE.vat}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
